/**
 * Copyright © 2025 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { login, clearLoginToken } from "../../store/authSlice";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AbilityContext } from "../../contexts/abilityContext";
import authManager from "../../utils/authManager";
import mfaService from "../../api/mfaService";

import { 
  InputAdornment,
  IconButton,
  Box,
} from "@mui/material";
import { 
  LoginFormContainer,
  LoginFormWrapper,
  StyledSubTitle,
  StyledDescription,
  StyledEmailField,
  StyledPasswordField,
  StyledForgotPasswordLink,
  StyledFormContainer,
  StyledContinueButton,
  StyledHelperText,
  ForgotPasswordContainer,
  CarouselContainer,
  FormContainer,
  logoContainerStyle,
} from "./styles";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { LoadingSpinner } from '../../components/loading_spinner/index'
import image from "../../assets/images/OT_logo_with_name_black.png"
import { useNotificationContext } from '../../contexts/notificationContext'
import { CarouselPage } from "../../components/carousel";
import banner from '../../assets/images/banner.jpg'
import MfaVerificationModal from '../../components/mfa_verification_modal';
import MfaEnrollmentModal from '../../components/mfa_enrollment_modal';
import { resolveLandingPath } from '../../constants/appModules';

const LoginForm = () => {
  const { updateAbility } = useContext(AbilityContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Where PrivateRoute/PrivateRoutePages sent the user from before bouncing them
  // here — carried through so a deep link (e.g. an emailed review link) survives
  // a login prompt instead of always landing on the dashboard.
  const redirectTo = searchParams.get('redirect_to');
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const dispatch = useDispatch();
  const [errors, setErrors] = useState(null);
  const { 
    loading, 
    error, 
    mfaRequired,
    enrollmentRequired,
    tempSessionId,
    passkeyOptions,
    mfaEnforcement,
  } = useSelector((state) => state.auth);
  const [showPassword, setShowPassword] = React.useState(false);
  const [status, setStatus] = React.useState(false);
  const [showMfaModal, setShowMfaModal] = React.useState(false);
  const [showEnrollmentModal, setShowEnrollmentModal] = React.useState(false);
  const [enrollmentMfaEnforcement, setEnrollmentMfaEnforcement] = React.useState(null);
  const showNotication = useNotificationContext();
  const carouselData = [
    {
      id: 1,
      image: banner,
    },
    {
      id: 2,
      image: banner,
    },
    {
      id: 3,
      image: banner,
    },
  ];


  useEffect(() => {
    const session = localStorage.getItem('session');
    if (session === 'expired') {
      showNotication('Your session has expired', 'error');
      localStorage.removeItem('session');
    }
    
    const checkExistingAuth = async () => {
      if (authManager.isAuthenticated()) {
        try {
          const { checkCurrentUser } = await import('../../api/authService');
          const admin = await checkCurrentUser();
          
          if (admin?.password_changed === false) {
            navigate('/admin/reset_password');
          } else if (admin) {
            navigate(resolveLandingPath(admin?.accessible_modules));
          }
        } catch (error) {
          console.error('Auth check failed:', error);
        }
      }
    };
    
    checkExistingAuth();
  }, [navigate, showNotication]);

  useEffect(() => {
    if (mfaRequired && tempSessionId && passkeyOptions) {
      setShowMfaModal(true);
    }
  }, [mfaRequired, tempSessionId, passkeyOptions]);

  useEffect(() => {
    if (!enrollmentRequired) return;

    setEnrollmentMfaEnforcement(mfaEnforcement || 'mandatory');
    setShowEnrollmentModal(true);
    dispatch(clearLoginToken());
  }, [
    enrollmentRequired,
    dispatch,
    mfaEnforcement,
  ]);

  useEffect(() => {
    if (error) {
      setErrors(prev => ({
        ...prev,
        username: error?.errors?.username,
        password: error?.errors?.password,
      }));
    } else {
      setErrors(null);
    }
  }, [error]);

  const completeAuthenticatedLogin = async (admin, options = {}) => {
    if (!admin) {
      dispatch(clearLoginToken());
      return;
    }

    try {
      setStatus(true);
      authManager.login(admin, {
        authToken: options.authToken,
      });

      if (admin.password_changed === false) {
        navigate('/admin/reset_password');
        return;
      }

      await authManager.handlePostLogin(navigate, updateAbility, showNotication, { redirectTo, ...options });
    } finally {
      dispatch(clearLoginToken());
      setStatus(false);
    }
  };

  const handleEmail = (value) => {
    setUsername(value);
    if (errors?.username) {
      setErrors({...errors, username: ''});
    }
  };

  const handlePassword = (value) => {
    setPassword(value);
    if (errors?.password) {
      setErrors({...errors, password: ''});
    }
  };

  const validateLogin = (submittedUsername = username, submittedPassword = password) => {
    const newErrors = {};
    if (!submittedUsername.trim()) {
      newErrors.username = "Email is required";
    }
    if (!submittedPassword.trim()) {
      newErrors.password = "Password is required";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = e?.target?.closest?.('form') || e?.currentTarget?.closest?.('form') || e?.currentTarget;
    const submittedUsername = form?.elements?.namedItem('username')?.value ?? username;
    const submittedPassword = form?.elements?.namedItem('password')?.value ?? password;

    setUsername(submittedUsername);
    setPassword(submittedPassword);

    const validationErrors = validateLogin(submittedUsername, submittedPassword);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const response = await dispatch(login({ username: submittedUsername, password: submittedPassword })).unwrap();

      if (response?.mfa_required || response?.enrollment_required) {
        return;
      }

      await completeAuthenticatedLogin(response?.admin, {
        authToken: response?.token,
      });
    } catch (_error) {
      // Rejected login requests are surfaced through redux error state.
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleForgotPassword = () => {
    navigate('admin/forgot_password');
  };

  const handleMfaSuccess = async (response) => {
    setShowMfaModal(false);
    
    if (response.admin) {
      try {
        await completeAuthenticatedLogin(response.admin, {
          authToken: response.token,
        });
      } catch (err) {
        console.error('Post-MFA login navigation error:', err);
        if (!window.location.pathname.includes('/admin/reports')) {
          showNotication('Navigation error after login', 'warning');
        }
      }
    }
  };

  const handleMfaCancel = () => {
    setShowMfaModal(false);
    dispatch(clearLoginToken());
    showNotication('MFA verification cancelled', 'error');
  };

  const handleEnrollmentSuccess = async () => {
    setShowEnrollmentModal(false);
    
    try {
      const { checkCurrentUser } = await import('../../api/authService');
      const currentAdmin = await checkCurrentUser();
      await completeAuthenticatedLogin(currentAdmin, {
        prefetchedAdmin: currentAdmin,
        successMessage: 'MFA setup completed successfully. You are now logged in.',
      });
    } catch (err) {
      console.error('Post-enrollment error:', err);
      navigate('/admin/dashboard');
    }
  };

  const handleEnrollmentSkip = async () => {
    const isOptionalEnforcement = enrollmentMfaEnforcement === 'optional';

    if (!isOptionalEnforcement) {
      setShowEnrollmentModal(false);
      authManager.logout(true);
      return;
    }

    try {
      await mfaService.optionalSkip();
      setShowEnrollmentModal(false);
      const { checkCurrentUser } = await import('../../api/authService');
      const currentAdmin = await checkCurrentUser();
      await completeAuthenticatedLogin(currentAdmin, {
        prefetchedAdmin: currentAdmin,
      });
    } catch (e) {
      if (e?.response?.status === 403) {
        showNotication(
          'You have reached the maximum number of MFA skips. Please complete setup to continue.',
          'error'
        );

        setEnrollmentMfaEnforcement('mandatory');
        return;
      }

      showNotication(
        'Unable to skip MFA enrollment. Please complete setup.',
        'error'
      );
    }
  };

  return (
    <React.Fragment>
      {/* MFA Verification Modal */}
      <MfaVerificationModal
        open={showMfaModal}
        tempSessionId={tempSessionId}
        passkeyOptions={passkeyOptions}
        username={username}
        onSuccess={handleMfaSuccess}
        onCancel={handleMfaCancel}
      />
      
      {/* MFA Enrollment Modal */}
      <MfaEnrollmentModal
        open={showEnrollmentModal}
        onSuccess={handleEnrollmentSuccess}
        onSkip={handleEnrollmentSkip}
        isSkipAllowed
        enforcement={enrollmentMfaEnforcement || 'mandatory'}
      />
      
      <LoginFormContainer>
        <CarouselContainer>
          <CarouselPage data={carouselData} />
        </CarouselContainer>
        <FormContainer>
          <LoginFormWrapper>
            <Box sx={logoContainerStyle}>
              <img src={image} width="55%" alt="OwnersTable Logo" />
            </Box>
            <StyledSubTitle>Welcome</StyledSubTitle>
            <StyledDescription>Enter your password for OwnersTable™ to continue</StyledDescription>

            <StyledFormContainer onSubmit={handleSubmit}>
              <StyledEmailField
                name="username"
                autoComplete="username"
                placeholder="Enter your email"
                onChange={(e) => handleEmail(e.target.value)}
                error={!!errors?.username}
                helperText={errors?.username}
              />

              <div>
                <StyledPasswordField
                  name="password"
                  autoComplete="current-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  onChange={(e) => handlePassword(e.target.value)}
                  error={!!errors?.password}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        onClick={handleClickShowPassword}
                      >
                        { showPassword ? <VisibilityOff /> : <Visibility /> }
                      </IconButton>
                    </InputAdornment>
                  }
                />
                <StyledHelperText>{!!errors?.password && `${errors?.password}`}</StyledHelperText>
              </div>

              <ForgotPasswordContainer>
                <StyledForgotPasswordLink onClick={handleForgotPassword}>Forgot password?</StyledForgotPasswordLink>
              </ForgotPasswordContainer>

              <StyledContinueButton 
                variant="contained"
                type="submit"
                disabled={loading || status}
              >
                { loading || status ? <LoadingSpinner isLoading={true} /> : 'Continue' }
              </StyledContinueButton>
            </StyledFormContainer>
          </LoginFormWrapper>
        </FormContainer>
      </LoginFormContainer>
    </React.Fragment>
  )
};
export default LoginForm;
